/* External dependencies */
import { applyMiddleware, combineReducers, createStore as createReduxStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction';
import { combineEpics, createEpicMiddleware, Epic } from 'redux-observable';
import { Action } from 'redux';
import { thunk, ThunkDispatch } from 'redux-thunk';

/* Local dependencies */
import { UserState, userReducer } from '../components/users/redux/reducer';
import getUserEpic from '../components/users/get-user/redux/epics';
import updateUserEpic from '../components/users/update-user/redux/epics';
import modalReducer, { ModalState } from '../components/web-app/components/modal/redux/reducer';
import authReducer, { AuthState } from '../features/auth/redux/reducer';
import { loginWithGoogleEpic, logoutEpic, signInWithCognitoEpic, signOutWithCognitoEpic } from '../features/auth/redux/epics';
import listDeviceReducer, { ListDeviceState } from '../components/devices/list-device/redux/reducer';
import { listDevicesEpic } from '../components/devices/list-device/redux/epics';
import { deviceReducer, DeviceState } from '../components/devices/redux/reducer';
import { createDeviceEpic } from '../components/devices/create-device/redux/epics';
import { updateDeviceEpic } from '../components/devices/update-device/redux/epics';
import { verifyDeviceEpic } from '../components/devices/verify-device/redux/epics';
import { deleteDeviceEpic } from '../components/devices/delete-device/redux/epics';
import subscribeDeviceReducer, { SubscribeDeviceState } from '../components/devices/subscribe-device/redux/reducer';
import subscribeOnCreateDeviceEpic, { unsubscribeOnCreateDeviceEpic } from '../components/devices/subscribe-device/redux/epics';

export interface RootState {
  userReducer: UserState;
  modalReducer: ModalState;
  authReducer: AuthState;
  listDeviceReducer: ListDeviceState;
  deviceReducer: DeviceState;
  subscribeDeviceReducer: SubscribeDeviceState;
}

// Using a type cast to ensure all epics can be combined regardless of their specific action types
const rootEpic = combineEpics<any, any, any>(
  signInWithCognitoEpic,
  signOutWithCognitoEpic,
  loginWithGoogleEpic,
  logoutEpic,
  createDeviceEpic,
  updateDeviceEpic,
  verifyDeviceEpic,
  deleteDeviceEpic,
  getUserEpic,
  updateUserEpic,
  subscribeOnCreateDeviceEpic,
  unsubscribeOnCreateDeviceEpic,
  listDevicesEpic,
);

const rootReducer = combineReducers({
  userReducer,
  modalReducer,
  authReducer,
  listDeviceReducer,
  deviceReducer,
  subscribeDeviceReducer,
});

export type AppDispatch = ThunkDispatch<RootState, unknown, Action> & typeof store.dispatch;

let store: ReturnType<typeof createStore>;

export function createStore() {
  const epicMiddleware = createEpicMiddleware<Action, Action, RootState, any>();
  const preloadedState = {};

  const reduxStore = createReduxStore(
    rootReducer,
    preloadedState,
    composeWithDevTools(applyMiddleware(epicMiddleware, thunk)),
  );

  epicMiddleware.run(rootEpic);

  return reduxStore;
}

store = createStore();
